import { useForm } from "react-hook-form";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { DatePicker } from "@/components/ui/date-picker";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useCandidateQuery } from "@/queries/candidate/candidate";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";

import {
  candidateAvailabilityFormSchema,
  CandidateAvailabilityFormSchema,
} from "./schema";

import { useUpdateCandidateProfile } from "@/mutations/candidate/profile/updateProfile";
import dateStringToLocalDate from "@/utils/dateStringToLocalDate";

const CandidateAvailabilityEditFormDialog = ({
  profileId,
}: {
  profileId: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const { candidate } = useCandidateQuery(profileId);
  const { updateCandidateProfile, isUpdatingCandidateProfile } =
    useUpdateCandidateProfile(profileId, () => setIsOpen(false));

  const form = useForm<CandidateAvailabilityFormSchema>({
    resolver: zodResolver(candidateAvailabilityFormSchema),
    mode: "onChange",
    defaultValues: {
      availabilityFullTime: candidate?.availabilityFullTime,
      availabilityFractional: candidate?.availabilityFractional,
      availabilityAdvisory: candidate?.availabilityAdvisory,
      availabilityBoard: candidate?.availabilityBoard,
      availabilityTime: candidate?.availabilityTime,
      availabilityStartDate:
        dateStringToLocalDate(candidate?.availabilityStartDate) ?? undefined,
    },
  });

  useEffect(() => {
    form.reset({
      availabilityFullTime: candidate?.availabilityFullTime,
      availabilityFractional: candidate?.availabilityFractional,
      availabilityAdvisory: candidate?.availabilityAdvisory,
      availabilityBoard: candidate?.availabilityBoard,
      availabilityTime: candidate?.availabilityTime,
      availabilityStartDate:
        dateStringToLocalDate(candidate?.availabilityStartDate) ?? undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, candidate]);

  const onSubmit = (data: CandidateAvailabilityFormSchema) => {
    updateCandidateProfile({
      emails: candidate?.emails ?? [],
      ...data,
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="link" className="edit-text self-end">
          Edit
        </Button>
      </DialogTrigger>
      <DialogContent className="w-full sm:max-w-2xl">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <DialogTitle>Edit Availability</DialogTitle>
          </div>
          <DialogDescription className="sr-only">
            Update your availability below.
          </DialogDescription>
        </DialogHeader>
        <ScrollArea className="sm:max-h-[calc(100vh-10rem)] md:max-h-[70vh]">
          <Form {...form}>
            <form
              className="space-y-4 w-full"
              onSubmit={(e) => {
                e.preventDefault();
                void form.handleSubmit((data) => onSubmit(data))();
              }}
            >
              <FormField
                control={form.control}
                name="availabilityFractional"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between">
                    <FormLabel>Fractional / Part-Time Roles</FormLabel>
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={(state) => {
                          field.onChange(
                            typeof state === "string" ? false : state,
                          );
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="availabilityAdvisory"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between">
                    <FormLabel>Advisory Roles</FormLabel>
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={(state) => {
                          field.onChange(
                            typeof state === "string" ? false : state,
                          );
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="availabilityBoard"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between">
                    <FormLabel>Board Roles</FormLabel>
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={(state) => {
                          field.onChange(
                            typeof state === "string" ? false : state,
                          );
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="availabilityFullTime"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between">
                    <FormLabel>Full-Time Roles</FormLabel>
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={(state) => {
                          field.onChange(
                            typeof state === "string" ? false : state,
                          );
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="availabilityTime"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Availability (hours/week)</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="availabilityStartDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Available Start Date</FormLabel>
                    <FormControl>
                      <DatePicker
                        value={field.value}
                        onChange={field.onChange}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="w-full flex justify-between">
                <Button
                  variant="outline"
                  className="w-fit"
                  type="button"
                  onClick={() => setIsOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  variant="secondary"
                  className="w-fit"
                  type="submit"
                  disabled={
                    !form.formState.isValid || isUpdatingCandidateProfile
                  }
                >
                  Save
                </Button>
              </div>
            </form>
          </Form>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default CandidateAvailabilityEditFormDialog;
